import { type ReactNode } from 'react';
import { useSnapshot } from 'valtio';

import * as userStore from '@cartwhl/common/stores/userStore';
import {
  formatOrgCreditsForDisplay,
  getOrgAvailableCredits,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';
import AppsMenu from '@cartwhl/ui/organisms/AppsMenu';
import UserMenu, { AccountCreditsLabel } from '@cartwhl/ui/organisms/UserMenu';

export default function UserMenuWithCredits({
  middleContent,
}: {
  middleContent?: ReactNode;
}) {
  const { loading: userLoading, userInfo, organizationInfo } = useSnapshot(
    userStore.state,
  );

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  const creditsLabel = orgCreditsWorkspace
    ? `${formatOrgCreditsForDisplay(orgAvailableCredits)} credits`
    : `${userInfo.spinsAvailable ?? 0} Spins`;

  const secondaryLabel =
    `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim() ||
    userInfo.email;

  return (
    <div className="flex shrink-0 items-center gap-6">
      <AppsMenu />
      {middleContent}
      <UserMenu
        trigger={
          <button
            type="button"
            className="outline-hidden hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1 transition-colors sm:gap-2 sm:px-2.5 sm:py-1.5"
            aria-label="Account menu"
          >
            {!userLoading ? (
              <AccountCreditsLabel
                creditsLabel={creditsLabel}
                secondaryLabel={secondaryLabel}
                className="pr-0.5"
              />
            ) : null}
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-visible pr-0.5">
              <Avatar userInfo={userInfo} className="relative">
                <span className="dark:bg-gray-90 absolute -bottom-0.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                  <ChevronDropdownIcon className="text-primary h-3 w-3 shrink-0" />
                </span>
              </Avatar>
            </span>
          </button>
        }
      />
    </div>
  );
}
